import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RsaEncryptionService {
  // Caché de la clave pública (se pide solo 1 vez)
  private cryptoKeyCache$: Observable<CryptoKey> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * PASO 1.1: Convertir clave PEM a CryptoKey
   * - Recibe la clave pública en formato PEM del backend
   * - La convierte a formato que Web Crypto API puede usar
   */
  private pemToCryptoKey(pem: string): Promise<CryptoKey> {
    // Remover headers del PEM
    const pemHeader = '-----BEGIN PUBLIC KEY-----';
    const pemFooter = '-----END PUBLIC KEY-----';
    
    const pemContents = pem
      .replace(pemHeader, '')
      .replace(pemFooter, '')
      .replace(/\s/g, '');

    // Convertir Base64 a ArrayBuffer
    const binaryString = window.atob(pemContents);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Importar como clave RSA-OAEP con SHA-256
    return window.crypto.subtle.importKey(
      'spki',                    // Formato de clave pública
      bytes.buffer,
      {
        name: 'RSA-OAEP',        // Algoritmo de cifrado
        hash: 'SHA-256'          // Función hash
      },
      true,
      ['encrypt']               // Solo para cifrar
    );
  }

  /**
   * PASO 1.2: Obtener clave pública del backend
   * - Hace GET a /public-key
   * - Cachea la clave para no pedirla de nuevo
   */
  getPublicKey(apiUrl: string): Observable<CryptoKey> {
    if (this.cryptoKeyCache$) {
      return this.cryptoKeyCache$;
    }

    this.cryptoKeyCache$ = this.http.get<any>(`${apiUrl}/public-key`).pipe(
      map(response => {
        if (!response.publicKey) {
          throw new Error('Respuesta inválida: falta publicKey');
        }
        
        let publicKey = response.publicKey;
        
        // Normalizar saltos de línea si vienen escapados
        if (publicKey.includes('\\n')) {
          publicKey = publicKey.replace(/\\n/g, '\n');
        }
        
        return publicKey;
      }),
      switchMap(pemKey => from(this.pemToCryptoKey(pemKey))),
      shareReplay(1)  // Cachear resultado
    );

    return this.cryptoKeyCache$;
  }

  /**
   * PASO 1.3: Cifrar datos
   * - Convierte objeto a JSON
   * - Cifra con RSA-OAEP
   * - Retorna string Base64
   */
  encrypt(data: any, apiUrl: string): Observable<string> {
    return this.getPublicKey(apiUrl).pipe(
      switchMap(cryptoKey => {
        const jsonData = JSON.stringify(data);
        
        // Validar tamaño (RSA-2048 con OAEP SHA-256 = máx 190 bytes)
        const maxSize = 190;
        if (jsonData.length > maxSize) {
          throw new Error(`Datos demasiado grandes: ${jsonData.length} bytes (máx: ${maxSize})`);
        }

        // Convertir string a bytes
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(jsonData);

        // Cifrar con Web Crypto API
        return from(
          window.crypto.subtle.encrypt(
            { name: 'RSA-OAEP' },
            cryptoKey,
            dataBuffer
          )
        );
      }),
      map(encryptedBuffer => {
        // Convertir ArrayBuffer a Base64
        const encryptedArray = new Uint8Array(encryptedBuffer);
        let binary = '';
        for (let i = 0; i < encryptedArray.byteLength; i++) {
          binary += String.fromCharCode(encryptedArray[i]);
        }
        return window.btoa(binary);
      })
    );
  }

  /**
   * Limpiar caché (usar si cambia el usuario o sesión)
   */
  clearCache(): void {
    this.cryptoKeyCache$ = null;
  }
}
